import { selector } from 'recoil';
import {
  validateCarNo,
  validateRegistBackNo,
  validateYearMonth,
} from '../../helper/validator';
import { JOB_TYP } from '../../types/types';
import { userInfo, personalInfo, mortgageInfo } from '../index';

const isActiveStep1 = selector<any>({
  key: 'isActiveStep1',
  get: ({ get }: any) => {
    const info = get(personalInfo);
    const str = (info.birthDt + info.gender + info.residentNo).substring(2);
    return validateRegistBackNo(str);
  },
});

const isActiveStep2 = selector<any>({
  key: 'isActiveStep2',
  get: ({ get }: any) => {
    const info = get(personalInfo);
    return !!info.objectTyp && !!info.hopefulAmt;
  },
});

const isActiveStep3 = selector<any>({
  key: 'isActiveStep3',
  get: ({ get }: any) => {
    const info = get(personalInfo);
    return Number(info.salaryAmt) !== 0 && !!info.salaryAmt && !!info.jobTyp;
  },
});

const isActiveStep4 = selector<any>({
  key: 'isActiveStep4',
  get: ({ get }: any) => {
    const info = get(personalInfo);

    switch (info.jobTyp) {
      case JOB_TYP.OFFICE_WORKER:
      case JOB_TYP.PUBLIC_OFFICIAL:
      case JOB_TYP.PROFESSION: {
        return (
          info?.joinDt?.length === 6 &&
          validateYearMonth({
            year: info.joinDt.substring(0, 4),
            month: info.joinDt.substring(4),
          }) &&
          !!info.employeeTyp?.value &&
          !!info.healthPayerTyp &&
          !!info.compNm
        );
      }
      case JOB_TYP.BUSINESS_MAN: {
        return (
          info?.joinDt?.length === 6 &&
          validateYearMonth({
            year: info.joinDt.substring(0, 4),
            month: info.joinDt.substring(4),
          }) &&
          !!info.businessTyp?.value &&
          !!info.healthPayerTyp &&
          !!info.compNm &&
          !!info.compRegNo
        );
      }
      case JOB_TYP.FREELANCER: {
        return (
          info?.joinDt?.length === 6 &&
          validateYearMonth({
            year: info.joinDt.substring(0, 4),
            month: info.joinDt.substring(4),
          }) &&
          !!info.healthPayerTyp
        );
      }
      case JOB_TYP.IN_OCCUPATION: {
        return true;
      }
      default: {
        return false;
      }
    }
  },
});

const isActiveStep5 = selector<any>({
  key: 'isActiveStep5',
  get: ({ get }: any) => {
    const info = get(personalInfo);
    let isActive = !!info.houseTyp && !!info.carOwnerYn && !!info.debtYn;

    if (info.carOwnerYn === 'Y') {
      isActive = isActive && validateCarNo(info.carNo);
    }

    if (info.debtYn === 'Y') {
      isActive = isActive && info.debtTyp;
    }

    return isActive;
  },
});

const saveConditions = selector<any>({
  key: 'saveCondition',
  get: ({ get }: any) => {
    const state = get(personalInfo);

    return {
      salaryAmt: state.salaryAmt,
      jobTyp: state.jobTyp.value,
      joinDt: state.joinDt,
      employeeTyp: state.employeeTyp.value,
      businessTyp: state.businessTyp.value,
      compRegNo: state.compRegNo,
      compNm: state.compNm,
      houseTyp: state.houseTyp,
      healthPayerTyp: state.healthPayerTyp,
      carOwnerYn: state.carOwnerYn,
      // objectTyp: state.objectTyp,
      // hopefulAmt: state.hopefulAmt,
      debtYn: state.debtYn,
      debtTyp: state.debtTyp,
      carNo: state.carOwnerYn === 'Y' ? state.carNo : undefined,
      reuseYn: state.reuseYn,
      scrapingYn: state.scrapingYn,
    };
  },
});

const hasBasePersonalInfo = selector<any>({
  key: 'hasBasePersonalInfo',
  get: ({ get }: any) => {
    const state = get(personalInfo);
    return !!state.custNm && !!state.cellNo && !!state.birthDt;
  },
});

const residentNoSelector = selector<any>({
  key: 'residentNo',
  get: ({ get }: any) => {
    const state = get(personalInfo);
    return state.residentNo;
  },
  set: ({ set }: any, newValue) => {
    set(personalInfo, (prevValue: any) => {
      return { ...prevValue, residentNo: newValue };
    });
  },
});

const beaconSelector = selector<any>({
  key: 'beaconSelector',
  get: ({ get }: any) => {
    const p = get(personalInfo);
    const u = get(userInfo);

    return {
      custSeq: p.custSeq,
      mediaCd: u.mediaCd,
      srcCd: u.srcCd,
    };
  },
});

const mortgageSelector = selector<any>({
  key: 'mortgageSelector',
  get: ({ get }: any) => {
    return get(mortgageInfo);
  },

  set: ({ set }: any, newValue) => {
    set(mortgageInfo, (prevValue: any) => {
      return { ...prevValue, [newValue.key]: newValue.value };
    });
  },
});

const mtgSavePreReqTempSelector = selector<any>({
  key: 'mtgSavePreReqTempSelector',
  get: ({ get }: any) => {
    const pInfo = get(personalInfo);
    const mInfo = get(mortgageInfo);
    return {
      custSeq: pInfo.custSeq,
      preRequestSeq: mInfo.preRequestSeq,
      purposeCd: mInfo.purposeCd?.value,
      haveCnt: mInfo.haveCnt?.value,
      disposalPlanYn: mInfo.disposalPlanYn?.value,
      houseType: mInfo.houseType?.value,
      ldongCode: mInfo.ldongCode?.value,
      apartment: mInfo.apartment?.value,
      floor: mInfo.floor?.value,
      dong: mInfo.dong,
      ho: mInfo.ho,
      hopefulAmt: mInfo.hopefulAmt,
      housePrice: mInfo.housePrice,
      previousYn: mInfo.previousYn?.value,
      previousAmt: mInfo.previousAmt,
      tenantYn: mInfo.tenantYn?.value,
      tenantAmt: mInfo.tenantAmt,
      marriageCd: mInfo.marriageCd?.value,
      jobType: mInfo.jobType?.value,
      salaryAmt: mInfo.salaryAmt,
      openDt: mInfo.openDt?.value,
      credPoint: mInfo.credPoint,
      firstYn: mInfo.firstYn?.value,
      rateSeq: mInfo.rateSeq?.value,
    };
  },
});

export {
  isActiveStep1,
  isActiveStep2,
  isActiveStep3,
  isActiveStep4,
  isActiveStep5,
  saveConditions,
  hasBasePersonalInfo,
  residentNoSelector,
  beaconSelector,
  mortgageSelector,
  mtgSavePreReqTempSelector,
};
